import { inject, Injectable } from '@angular/core';
import { StoragePluginService } from './plugin';
import {
  concatStatusUpdates,
  createNewStatusUpdate,
  RouteStatusUpdate,
  RouteStatusUpdateResult,
  RouteUpdate,
} from '../types';
import { environment } from 'src/environments/environment';
import { UtilityService } from './utility/utility.service';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  delay,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  from,
  interval,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { DateTime } from 'luxon';
import { ServerProxyService } from './server-proxy.service';
import { AppData } from '../data';
import { RouteSetupService } from './route-setup.service';
import { LogoutService } from './logout.service';
import { LoadingController } from '@ionic/angular';

@Injectable({ providedIn: 'root' })
export class UpdatesService {
  private _data = inject(AppData);
  private _loadingCtrl = inject(LoadingController);
  private _logoutService = inject(LogoutService);
  private _proxy = inject(ServerProxyService);
  private _routeSetupService = inject(RouteSetupService);
  private _storageService = inject(StoragePluginService);
  private _utilityService = inject(UtilityService);

  private _lastEventTime: DateTime | null = DateTime.now();
  private _processing: boolean = false;
  private _serverHasPendingUpdates = false;

  private messageId: string = this._utilityService.generateGuid();

  private readonly pendingUpdatesStorageKey = 'Pending_Updates';
  private readonly inProcessUpdatesStorageKey = 'In_Process_Updates';

  private readonly _sendPendingUpdates$ = new Subject<boolean>();

  private readonly gtm = (<any>window).dataLayer || [];

  public readonly pendingUpdatesExist$ = new BehaviorSubject<boolean>(false);
  /** has an update for one or more jobs */
  private _hasSignificantUpdates = false;
  private _processingSignificantUpdates = false;

  private async _presentLoadingSpinner(message: string): Promise<void> {
    if (!environment.features.silentTreatment.blockUI || !this._data.connectionStatus?.connected) {
      return;
    }

    const topLoader = await this._loadingCtrl.getTop();
    if (!topLoader) {
      const loader = await this._loadingCtrl.create({ message });
      await loader.present();
      console.info('BLOCK UI');
    }
  }

  private async _dismissLoadingSpinner(): Promise<void> {
    try {
      const topLoader = await this._loadingCtrl.getTop();
      if (topLoader) {
        await this._loadingCtrl.dismiss();
        console.info('UNBLOCK UI');
      }
    } catch (err) {
      console.error('Failed to dismiss loading spinner', err);
    }
  }

  /** throttle logic */
  private throttleHandler$: Observable<boolean> = interval(1000).pipe(
    // 1. Check if pending updates exist
    switchMap(() => this._pendingUpdatesKeysExist()),
    // 2. Evaluate guard conditions and map to an explicit boolean emission
    map((pendingUpdatesExist): boolean => {
      const { debounce, strategy } = environment.features.silentTreatment;

      // Condition 1: Synchronous strategy is busy processing
      if (strategy === 'sync' && this._processing) {
        return false;
      }

      // Condition 2: No pending updates to look at
      if (!pendingUpdatesExist) {
        return false;
      }

      // Condition 3: Network is down
      if (!this._data.connectionStatus?.connected) {
        return false;
      }

      // Condition 4: Throttling / Debounce window is still active
      //  debounce updates: if the last update was less than 10 seconds ago, keep throttling
      //  note: gps reports get sent every 30 seconds
      const isTooRecent =
        this._lastEventTime &&
        this._lastEventTime.valueOf() > DateTime.now().minus({ seconds: debounce }).valueOf();
      if (isTooRecent) {
        return false;
      }

      // All guards passed: Proceed
      return true;
    }),
    tap(shouldProcess => {
      if (shouldProcess) {
        this._processing = true;
      }
    }),
  );

  /** listens for updates and manages the update cycle */
  public updatesOrchestrator$ = this._sendPendingUpdates$.pipe(
    // Evaluate guards before continuing downstream
    switchMap(() => this.throttleHandler$),

    // Only proceed if guard emits a value of `true`
    filter(shouldProcess => shouldProcess === true),

    exhaustMap(() =>
      from(this._getRouteStatusUpdate()).pipe(
        // Ensure we actually have updates to send
        filter(updates => !!updates),
        // send updates to server
        concatMap(routeStatusUpdates => this._sendUpdatesToServer(routeStatusUpdates)),
        // handle server response variations
        concatMap(async (result): Promise<boolean> => {
          // user has been logged out, move to handleLogout flow
          if (result.loggedOutDate) {
            await this._handleLogout(result);
            return false;
          }
          // need to get updates from server
          if (
            (result.needUpdateFromServer || this._serverHasPendingUpdates) &&
            this._data.driverIsActivelyReporting
          ) {
            return true;
          }
          // no updates to fetch, no need to continue
          return false;
        }),
        filter(needServerUpdate => needServerUpdate === true),
        tap(() => {
          this._serverHasPendingUpdates = true;
          this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_get_route_update' });
        }),
        //  Eco race condition throttle
        delay(environment.features.silentTreatment.throttle),

        //  Get updates from server
        concatMap(async () => {
          const routeUpdate = await this._proxy.routeUpdate();
          this._serverHasPendingUpdates = false;

          if (routeUpdate) {
            this._data.set('noShowOptions', routeUpdate.noShowOptions || []);
            await this._routeSetupService.routeSetup(routeUpdate);
          }
        }),

        // Global cleanup block for this individual execution run
        finalize(async () => {
          this._processing = false;
          // Re-evaluate queue status
          const pendingExists = await this._pendingUpdatesKeysExist();
          this.pendingUpdatesExist$.next(pendingExists);
        }),
      ),
    ),
    catchError(error => {
      console.error('Fatal orchestrator failure', error);
      this._processing = false;
      return of(null);
    }),
  );

  public async addPendingUpdate({
    update,
    isSignificant = false,
  }: {
    update: RouteStatusUpdate;
    isSignificant: boolean;
  }) {
    this.pendingUpdatesExist$.next(true);
    this._lastEventTime = DateTime.now();
    const currentTicks = new Date().getTime() + Math.floor(Math.random() * 9999) + 1;
    await this._storageService.set(
      this.pendingUpdatesStorageKey + currentTicks,
      JSON.stringify(update),
    );
    if (isSignificant) {
      this._hasSignificantUpdates = true;
    }
    this._sendPendingUpdates$.next(true);
  }

  async forceRemovePendingUpdates(): Promise<RouteStatusUpdate | null> {
    this.gtm.push({
      event: 'custom_event',
      custom_event_name: 'perform_ force_remove_pending_updates',
    });
    const _pendingUpdatesExist = await this._pendingUpdatesKeysExist();
    if (!_pendingUpdatesExist) {
      return null;
    }
    await this._removePendingUpdates();
    const _pendingUpdatesKeysExist = await this._pendingUpdatesKeysExist();
    this.pendingUpdatesExist$.next(_pendingUpdatesKeysExist);
    return null;
  }

  /**
   * Trigger orchestrator if pending updates exist
   * Called from login page on app reopen to handle app kill scenario
   * Returns true if pending updates were found and trigger was initiated
   */
  public async triggerSyncIfPendingUpdatesExist(): Promise<boolean> {
    const hasPending = await this._pendingUpdatesKeysExist();

    // Guard 1: No pending updates - nothing to do
    if (!hasPending) {
      this.pendingUpdatesExist$.next(false);
      return false;
    }

    // Guard 2: Orchestrator already running - avoid duplicate trigger
    if (this._processing) {
      this.pendingUpdatesExist$.next(true);
      return true;
    }

    // All guards passed: Initialize observable and trigger orchestrator
    console.info('Triggering orchestrator for pending updates from previous session');
    this.pendingUpdatesExist$.next(true);
    this._sendPendingUpdates$.next(true);

    return true;
  }

  private async _pendingUpdatesKeysExist(): Promise<boolean> {
    let keys = await this._storageService.keys();
    keys = keys.filter(key => key.startsWith(this.pendingUpdatesStorageKey));
    return keys.length > 0;
  }

  private async _removePendingUpdates(): Promise<RouteStatusUpdate> {
    let keys = await this._storageService.keys();
    keys = keys.filter(key => key.startsWith(this.pendingUpdatesStorageKey));
    if (keys.length === 0) {
      return null;
    }

    // set the messageId if not already set.
    this.messageId = this.messageId || this._utilityService.generateGuid();

    // assume all pending updates have the same sessionId
    const rawPendingUpdate = await this._storageService.get(keys?.[0]);
    const pendingUpdate: RouteStatusUpdate = JSON.parse(rawPendingUpdate);
    const blankUpdate: RouteStatusUpdate = createNewStatusUpdate(pendingUpdate?.sessionId);
    blankUpdate.messageId = this.messageId;

    // merge all of the sessions in storage currently into one RouteStatusUpdate
    for (let i = 0; i < keys.length; i++) {
      const update = await this._storageService.get(keys[i]);
      const parsed: RouteStatusUpdate = JSON.parse(update);
      concatStatusUpdates(blankUpdate, parsed);
      await this._storageService.remove(keys[i]);
    }

    return blankUpdate;
  }

  private async _getRouteStatusUpdate(): Promise<RouteStatusUpdate> {
    console.info('Getting Updates');

    // get & remove the pending updates from local storage
    const routeStatusUpdates = await this._removePendingUpdates();

    // store them in pending updates just in case the client dies before the response is completed
    // this is just coop over complicating for edge cases
    await this._storageService.set(
      this.inProcessUpdatesStorageKey,
      JSON.stringify(routeStatusUpdates),
    );
    this._processingSignificantUpdates = this._hasSignificantUpdates;
    this._hasSignificantUpdates = false;

    return routeStatusUpdates;
  }

  /** Sends the updates to the server. Includes retry and error handling logic */
  private _sendUpdatesToServer(
    routeStatusUpdate: RouteStatusUpdate,
  ): Observable<RouteStatusUpdateResult> {
    return from(this._proxy.routeStatusUpdate(routeStatusUpdate)).pipe(
      tap(() => (this.messageId = this._utilityService.generateGuid())),
      catchError(async err => {
        console.log('Failed to send updates to the server', { error: err, routeStatusUpdate });
        console.error('Failed to send updates to the server, returning them to the queue');
        //  if we fail all three attempts, then add the updates back into the queue

        // TODO: This places older records behind newer records in your storage.
        // Consider rewriting this logic to prepend items or preserve timestamps.
        await this.addPendingUpdate({
          update: {
            ...routeStatusUpdate,
            // this doesn't double count bc it only increments more after proxy starts throwing errors
            retryCount: routeStatusUpdate.retryCount + 1,
          },
          isSignificant: this._processingSignificantUpdates,
        });
        this._processingSignificantUpdates = false;
        const routeStatusUpdateResult = {
          loggedOutDate: null,
          needUpdateFromServer: false,
        } as RouteStatusUpdateResult;
        return routeStatusUpdateResult;
      }),
      finalize(() => {
        void (async () => {
          console.debug('🧹 Clean up');
          await this._storageService.remove(this.inProcessUpdatesStorageKey);
          await this._dismissLoadingSpinner();
        })();
      }),
    );
  }

  private async _handleLogout(response: RouteStatusUpdateResult) {
    // the response says the driver is logged out but the driver is still active on the route
    console.info('⚠️ User is logged out. Redirecting to login page..', response);
    // stop making new update reports since the driver is now logged out and should be back on the login
    if (environment.features.dumpUpdatesOnForcedLogout || response.desync) {
      await this.forceRemovePendingUpdates();
    }
    await this._logoutService.handleWasLoggedOut(response);
  }

  private async _handleGetUpdateFromServer() {
    try {
      this._serverHasPendingUpdates = true;
      const { throttle, noSignificantEvents } = environment.features.silentTreatment;

      if (noSignificantEvents && this._hasSignificantUpdates) {
        console.info('has significant updates in memory, will pull next time around');
        return null;
      }

      console.info('...waiting 30 seconds before requesting server updates');
      this.gtm.push({ event: 'custom_event', custom_event_name: 'perform_ get_route_update' });
      const routeUpdate: RouteUpdate | null = await new Promise(resolve => {
        setTimeout(async () => {
          console.info('☎️  Requesting updates from server');
          const _routeUpdate = await this._proxy.routeUpdate();
          this._serverHasPendingUpdates = false;
          resolve(_routeUpdate);
        }, throttle);
      });
      console.info('Updates received from server');
      // routeUpdate will be null if there were no changes
      if (!routeUpdate) {
        return null;
      }

      this._data.set('noShowOptions', routeUpdate.noShowOptions || []);
      return this._routeSetupService.routeSetup(routeUpdate);
    } catch (err) {
      console.error('Error occurred retrieving latest schedule from server', err);
      this._serverHasPendingUpdates = true;
      return null;
    } finally {
      await this._dismissLoadingSpinner();
    }
  }
}
